import React from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserId } from '@common-reducers/UserSelectors';
import { useGetProTrialStatusQuery } from '@common-reducers/backend-api/backend-api';
import { resolveCtaUrl } from '../nudge-banner/nudge-banner-logic';
import { PLANS_PATH, trialDaysLeft } from './ext-trial-logic';

/**
 * #693: small persistent "Pro trial · N days left" chip in the extension top bar. Days left come from
 * the server (`pro-trial-status`), never from the signup time. Click opens the plans page.
 * Fail-closed: no user, no live trial, or a fetch error renders nothing.
 */
const ExtTrialChip: React.FC = () => {
  const { t } = useTranslation(namespace);
  const userId = useAppSelector(selectUserId);
  const { data } = useGetProTrialStatusQuery(undefined, { skip: !userId });
  const daysLeft = trialDaysLeft(data);
  if (daysLeft === null) return null;

  const open = () => {
    try {
      window.open(resolveCtaUrl(PLANS_PATH, 'extension'), '_blank', 'noopener,noreferrer');
    } catch {
      // never break the top bar
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      data-testid="ext-trial-chip"
      aria-label={`${t('extTrial.chip', { count: daysLeft })} · ${t('extTrial.chipTitle')}`}
      style={{
        border: '1px solid #bfdbfe',
        background: '#eff6ff',
        color: '#1d4ed8',
        borderRadius: 999,
        padding: '4px 10px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {t('extTrial.chip', { count: daysLeft })}
    </button>
  );
};

export default ExtTrialChip;
